import type { AllergensCopy } from '../types/allergens';

export const allergensDe: AllergensCopy = {
  seo: {
    title: 'EU-Allergene: Die 14 und das Gesetz erklärt',
    description:
      'Die 14 EU-Allergene und was das Gesetz von Restaurants verlangt - verständlich erklärt. Erfahren Sie, wie Sie Allergene auf Ihrer Speisekarte deklarieren, und welche versteckten Quellen Küchen überraschen.',
  },
  hero: {
    eyebrow: 'Leitfaden für Restaurantbetreiber',
    headingPre: 'Die 14 EU-Allergene, erklärt in',
    headingScribble: 'einfacher Sprache',
    body: 'Erfahren Sie, welche Allergene das Gesetz erfasst, wo sie sich verstecken und wie Sie Ihre Gäste klar informieren. Dieser Leitfaden begleitet Sie durch die Gerichte und die Regeln - ganz ohne Juristendeutsch.',
    factAllergenCount: '14 Allergene',
    factRegulation: 'Verordnung (EU) Nr. 1169/2011',
    factReadTime: '6 Min. Lesezeit',
    imageAlt: 'Ein Restauranttisch mit einer SafestMenu-QR-Speisekarte',
  },
  intro: {
    leadPre: 'Wenn Sie in der EU Speisen servieren, ist die Nennung dieser 14 Allergene sowohl',
    leadScribble1: 'gesetzlich vorgeschrieben',
    leadMid: 'als auch',
    leadScribble2: 'Teil guter Gastfreundschaft',
    leadPost: '.',
    body: 'Diese Allergene verstecken sich an mehr Stellen, als die meisten Speisekarten zugeben. Diese Seite erklärt die 14 Allergene, das Gesetz dahinter und wie Sie sie klar deklarieren - einfach und praxisnah, damit Sie noch heute handeln können.',
    stats: [
      { big: '14', small: 'Stoffe auf einer festen Liste' },
      { big: 'Jedes', small: 'Restaurant, Café und jede Bar in der EU' },
      { big: 'Klar', small: 'benannt, nie dem Zufall überlassen' },
    ],
  },
  whyItMatters: {
    eyebrow: 'Warum es wichtig ist',
    heading: 'Sichere Gäste sind die, die wiederkommen.',
    imageAlt: 'Gäste, die gemeinsam an einem Restauranttisch eine Speisekarte betrachten',
    cards: [
      {
        title: 'Eine Reaktion kann ernst sein',
        text: 'Für manche Gäste ist eine versteckte Spur keine Vorliebe - sie kann sie ins Krankenhaus bringen.',
      },
      {
        title: 'Vertrauen ist Gastfreundschaft',
        text: 'Stimmt es, entspannen sich die Gäste. Klare Allergen-Informationen gehören zu einem herzlichen Empfang, nicht zur Bürokratie.',
      },
      {
        title: 'Es ist Gesetz',
        text: 'Das Restaurant ist der verantwortliche Lebensmittelunternehmer, deshalb liegt die Pflicht bei Ihnen, nicht bei Ihrem Lieferanten.',
      },
    ],
  },
  theFourteen: {
    eyebrow: 'Die Übersicht',
    heading: 'Die vierzehn im Überblick',
    intro: 'Das sind die Allergene aus Anhang II der Verordnung (EU) Nr. 1169/2011. Es ist eine feste, abschließende Liste - Sie entscheiden nicht selbst, welche Sie erfassen, und jedes Gericht, das eines enthält, muss dies angeben.',
    gotchas: {
      heading: '3 Fallstricke, die Küchen überraschen',
      items: [
        'Erdnussöl zählt immer als Erdnuss, ob raffiniert oder nicht. Nur vollständig raffiniertes Sojaöl ist ausgenommen - übertragen Sie das nie auf Erdnussöl oder Arachisöl.',
        'Hafer ist von Natur aus glutenfrei, wird beim Mahlen aber fast immer kreuzkontaminiert. Behandeln Sie normalen Hafer als glutenhaltig, sofern er nicht als glutenfrei zertifiziert ist.',
        'Sojalecithin (E322) steckt in einem großen Teil von Schokolade, Aufstrichen und Backwaren - prüfen Sie deshalb die Lieferantenblätter, nicht nur die sichtbaren Zutaten.',
      ],
    },
  },
  legislation: {
    eyebrow: 'Das Gesetz in Kürze',
    heading: 'Verordnung (EU) Nr. 1169/2011',
    paragraphs: [
      'Die Regeln stammen aus der Verordnung (EU) Nr. 1169/2011, die oft als Lebensmittelinformationsverordnung (LMIV) bezeichnet wird. Als EU-Verordnung gilt sie unmittelbar in jedem Mitgliedstaat, auch in Rumänien, ohne dass eine nationale Umsetzung nötig ist.',
      'Sie gilt seit dem 13. Dezember 2014. Artikel 9 listet auf, welche Informationen Lebensmittel tragen müssen, Artikel 21 regelt, wie Allergene auf verpackten Lebensmitteln hervorgehoben werden, und Anhang II enthält die Liste der 14 Allergene selbst.',
      'Für lose abgegebene Speisen - also Gerichte, die Sie auf Bestellung zubereiten - macht Artikel 44 Allergen-Informationen verpflichtend, überlässt die Methode aber jedem Land selbst. Auch die Sanktionen werden national festgelegt, nach dem jeweiligen Lebensmittelrecht des Landes, nicht durch die Verordnung selbst.',
    ],
    asksHeading: 'Was von Ihnen verlangt wird',
    asksItems: [
      'Benennt 14 Allergene, die Sie deklarieren müssen',
      'Gilt für Speisekarten und Etiketten gleichermaßen',
      'Erfasst vorverpackte und lose abgegebene Speisen',
      'Muss korrekt sein und aktuell gehalten werden',
    ],
    natashasLaw:
      "Eine Regelung, von der Sie vielleicht schon gehört haben, Natasha's Law im Vereinigten Königreich (UK), ist eine britische Vorschrift aus dem Jahr 2021 - strenger als der EU-Mindeststandard und überhaupt kein Teil des EU-Rechts.",
  },
  steps: {
    eyebrow: 'So deklarieren Sie richtig',
    heading: 'Machen Sie aus der Pflicht eine einfache Routine',
    intro: 'Diese 4 Schritte führen Sie von einem Stapel Rezepte zu Allergen-Informationen, denen Gäste vertrauen können.',
    items: [
      {
        title: 'Schaffen Sie eine einzige verlässliche Quelle',
        text: 'Markieren Sie für jedes Gericht, welche der 14 Allergene enthalten sind - abgeleitet aus Ihren Rezepten und Lieferantenangaben. Bewahren Sie das an einem Ort auf, damit Küche und Service immer übereinstimmen.',
      },
      {
        title: 'Zeigen Sie es dort, wo Gäste es sehen',
        text: 'Zeigen Sie es standardmäßig schriftlich: auf der Speisekarte, auf einer übersichtlichen Tafel oder auf einer digitalen Speisekarte. Schriftlich ist immer besser, als sich aufs Erinnern zu verlassen.',
      },
      {
        title: 'Schulen Sie Ihr Team',
        text: 'Der Service sollte aus der Aufzeichnung vorlesen und wissen, wann in der Küche nachgefragt werden muss, statt zu raten. So stimmen die Antworten jedes Mal.',
      },
      {
        title: 'Halten Sie es aktuell',
        text: 'Jede Rezeptänderung oder ein neuer Lieferant kann ein Allergen hinzufügen oder entfernen. Aktualisieren Sie die Aufzeichnung, sobald sich etwas ändert.',
      },
    ],
    footnote: 'Prüfen Sie erneut, wann immer sich ein Rezept oder ein Lieferant ändert - genau dort veralten die meisten Speisekarten unbemerkt.',
  },
  comparison: {
    eyebrow: 'Wo die Angabe erfolgt',
    heading: 'Vorverpackt oder lose abgegeben',
    intro: 'Dieselben 14 Allergene, angegeben an unterschiedlichen Stellen - je nachdem, wie das Essen verkauft wird. Das ist die Verwechslung, die wir am häufigsten sehen.',
    leftTitle: 'Vorverpackte Lebensmittel',
    leftPoints: [
      'Verkauft in der Verpackung, in die es schon vor der Bestellung abgefüllt wurde, wie eine Sauce in der Flasche.',
      'Benötigt eine vollständige Zutatenliste, in der die 14 Allergene hervorgehoben sind (Artikel 21).',
      'Trägt außerdem die übrigen Kennzeichnungsangaben: Bezeichnung, Menge und Haltbarkeitsdatum.',
    ],
    rightTitle: 'Lose abgegebene Speisen',
    rightPoints: [
      'Gerichte, die Sie auf Bestellung zubereiten und servieren, sowie Speisen, die vor Ort verpackt werden, wenn ein Gast danach fragt.',
      'Keine gedruckte Zutatenliste nötig, Allergen-Informationen sind aber verpflichtend (Artikel 44).',
      'In der Form, die Ihr Land zulässt, aber immer korrekt und vor der Bestellung leicht zugänglich.',
    ],
  },
  crossContact: {
    eyebrow: 'In der Küche',
    heading: 'Kreuzkontamination und „kann Spuren enthalten“',
    imageAlt: 'Eine saubere, frisch abgewischte Arbeitsfläche in der Küche',
    bodyPre:
      'Allergene zu deklarieren, die Zutaten sind, ist die eine Aufgabe. Kreuzkontamination ist eine andere - ein Allergen, das durch gemeinsam genutzte Fritteusen, Schneidebretter oder Messer versehentlich übergeht.',
    bodyScribble: 'Eine Spur reicht aus',
    bodyPost:
      ', damit „kann Spuren enthalten“ erst dann ehrlich ist, wenn Sie das tatsächliche Risiko geprüft haben und es nicht beseitigen können.',
    tiles: [
      'Getrennte Vorbereitung und Utensilien',
      'Zwischen Bestellungen gründlich reinigen',
      'Das gesamte Team schulen',
      'Ehrlich mit „kann Spuren enthalten“ umgehen',
    ],
  },
  digital: {
    eyebrow: 'Wo eine QR-Speisekarte hilft',
    heading: 'Eine Änderung, aktuell an jedem Tisch',
    body: 'Gedruckte Speisekarten und laminierte Tafeln haben dieselbe Schwachstelle: aktuell zu bleiben. Eine digitale Speisekarte beseitigt das. Sie markieren jedes Gericht einmal, und eine Änderung erreicht sofort jeden Gast - ohne Nachdruck.',
    list: [
      'Die 14 Allergene pro Gericht einmal markieren',
      'Gäste filtern die Speisekarte auf das, was für sie sicher ist',
      'Immer die aktuelle Version, in ihrer eigenen Sprache',
    ],
    footnote: 'Ehrlich zu den Grenzen: Eine QR-Speisekarte macht Ihre Daten nicht automatisch korrekt und ersetzt keine Mitarbeiterschulung. Sie behebt veraltete Informationen - das häufigste Problem in der Praxis.',
    ctaLabel: 'So handhabt SafestMenu Allergene',
    qrImageAlt: 'Eine SafestMenu-QR-Code-Tischkarte neben einem Kaffee',
    dishImageAlt: 'Ein angerichtetes Gericht, dargestellt auf einer digitalen SafestMenu-Speisekarte',
    journeyEyebrow: 'Der Weg der Allergen-Information',
    journeyIntro: 'Allergen-Informationen durchlaufen 4 Übergaben. Ihre Speisekarte - und ein Werkzeug wie SafestMenu - steht an dem Punkt, an dem sie den Gast erreicht.',
  },
  faq: {
    eyebrow: 'Häufige Fragen',
    heading: 'Kurz beantwortet',
    items: [
      {
        q: 'Wie viele Allergene muss ich deklarieren?',
        a: 'Alle 14. Das EU-Recht legt in Anhang II der Verordnung 1169/2011 eine feste Liste fest, und jedes Gericht, das eines davon enthält, muss dies angeben. Sie können auch auf andere Dinge hinweisen, nach denen Gäste fragen, aber die 14 sind das gesetzliche Minimum.',
      },
      {
        q: 'Reicht es, Gästen zu sagen, sie sollen das Personal fragen?',
        a: 'Das hängt von Ihrem Land ab. Manche Mitgliedstaaten erlauben Allergen-Informationen auf Nachfrage, wenn ein deutlicher Hinweis Gäste zum Nachfragen auffordert und das Personal korrekte Antworten gibt. Andere, wie Frankreich, verlangen die schriftliche Angabe. Die schriftliche Angabe ist überall die sicherere Standardlösung.',
      },
      {
        q: "Gilt Natasha's Law für mein Restaurant?",
        a: "Nur, wenn Sie im Vereinigten Königreich (UK) tätig sind. Natasha's Law ist eine britische Vorschrift vom 1. Oktober 2021 für Lebensmittel, die vor Ort verpackt werden, bevor ein Gast bestellt, und sie ist strenger als der EU-Mindeststandard. Sie gilt nicht in Rumänien oder im übrigen EU-Raum.",
      },
      {
        q: 'Ist Erdnussöl von der Deklarationspflicht ausgenommen?',
        a: 'Nein. Erdnussöl zählt als Erdnuss, ob raffiniert oder nicht, und muss deshalb immer deklariert werden. Nur vollständig raffiniertes Sojaöl hat diese Ausnahme, und Arachisöl ist lediglich eine andere Bezeichnung für Erdnussöl.',
      },
      {
        q: 'Wer haftet, wenn die Allergen-Angaben falsch sind?',
        a: 'Sie. Das Restaurant ist der Lebensmittelunternehmer, deshalb liegt die Pflicht zu korrekten Angaben bei Ihnen - auch wenn ein Lieferant ein Rezept geändert hat, ohne Sie zu informieren. Deshalb ist eine einzige, aktuelle Aufzeichnung so wichtig.',
      },
      {
        q: 'Macht mich eine QR-Speisekarte allein schon rechtskonform?',
        a: 'Nein, und das würden wir auch nicht behaupten. Eine digitale Speisekarte hält Ihre Allergen-Informationen an jedem Tisch gleichzeitig aktuell, aber die Daten sind nur so gut wie das, was Sie eingeben. Sie brauchen weiterhin geschultes Personal und einen echten Prozess gegen Kreuzkontamination in der Küche.',
      },
    ],
  },
  closingNote: {
    heading: 'Ein Hinweis zu diesem Leitfaden',
    body: 'Dies ist eine allgemeine Orientierungshilfe, keine Rechtsberatung. Die nationalen Regelungen nach Artikel 44 unterscheiden sich innerhalb der EU und werden aktualisiert - prüfen Sie deshalb die Vorschriften an Ihrem Standort oder wenden Sie sich an Ihre nationale Lebensmittelbehörde.',
  },
  emailCapture: {
    heading: 'Diesen Leitfaden griffbereit haben',
    title: 'Erhalten Sie die Allergen-Checkliste per E-Mail',
    description: 'Wir senden Ihnen diesen Leitfaden sowie gelegentliche praktische Hinweise zu Speisekarten und Allergenkennzeichnung. Kein Spam, jederzeit abbestellbar.',
    buttonLabel: 'An mich senden',
  },
  cta: {
    heading: 'Bringen Sie Ihre Allergene auf eine Speisekarte, der Gäste vertrauen',
    body: 'Markieren Sie die 14 Allergene pro Gericht, aktualisieren Sie einmal, und jeder Gast sieht die Änderung direkt am Tisch. Sie führen eine Aufzeichnung, die aktuell bleibt, und Gäste filtern die Speisekarte auf das, was für sie sicher ist.',
    buttonLabel: 'SafestMenu kostenlos testen',
  },
  diagrams: {
    journey: [
      { title: 'Das Gericht', sub: 'Was wirklich drin ist, bis hin zur Sauce' },
      { title: 'Die Aufzeichnung', sub: 'Jedes Gericht den 14 zugeordnet' },
      { title: 'Die Speisekarte', sub: 'Dem Gast gezeigt, auf Papier oder Bildschirm', tag: 'SafestMenu' },
      { title: 'Der Gast', sub: 'Trifft eine Wahl, der er vertrauen kann' },
    ],
    routes: [{ label: 'Auf der Speisekarte' }, { label: 'Auf einem Schild' }, { label: 'Auf Nachfrage genannt' }],
    orLabel: 'oder',
    ingredients: {
      label: 'Zutaten',
      segments: [
        { text: 'Weizenmehl', mark: true },
        { text: ', Wasser, Rapsöl, ' },
        { text: 'Ei', mark: true },
        { text: ', Zucker, ' },
        { text: 'Milch', mark: true },
        { text: ', Salz, Gerstenmalz.' },
      ],
      note: 'Allergene innerhalb der Zutatenliste hervorgehoben.',
    },
  },
  reference: {
    ariaLabel: 'Die 14 EU-Allergene',
    allergenLabel: 'Allergen',
    commonlyIn: 'Häufig enthalten in',
    hidesIn: 'Versteckt sich in',
    remember: 'Merken Sie sich',
    footnotePre: 'Tippen Sie auf eines der ',
    footnotePost: ', um zu sehen, wo es sich versteckt. Die Beispiele sind ein Ausgangspunkt - prüfen Sie immer Ihre eigenen Rezepte und Lieferantenangaben.',
  },
  items: {
    'cereals-gluten': {
      name: 'Getreide mit Gluten',
      short: 'Weizen, Roggen, Gerste, Hafer, Dinkel',
      common: 'Brot, Pasta, Pizzateig, Kuchen, Gebäck, Teig, Panade, Couscous, Bier und die meisten mit Mehl gebundenen Saucen und Bratensoßen.',
      hidden: 'Sojasauce (meist mit Weizen fermentiert), Brühwürfel und Soßengranulat, Malzessig, manche Würste und Speiseeis (Paniermehl- oder Weizenfüllstoffe) sowie Gewürzmischungen.',
      note: 'Hafer ist von Natur aus glutenfrei, wird beim Mahlen aber fast immer kreuzkontaminiert. Behandeln Sie ein einfaches Hafergericht nicht als sicher für Zöliakiebetroffene, sofern es nicht als glutenfrei zertifiziert ist.',
    },
    crustaceans: {
      name: 'Krebstiere',
      short: 'Garnelen, Krabbe, Hummer, Scampi',
      common: 'Garnelen, Shrimps, Krabbe, Hummer, Languste, Flusskrebs und Scampi.',
      hidden: 'Garnelenpaste (Belacan) in thailändischen und südostasiatischen Currys, Meeresfrüchtefond in Suppen und Risottos, Bouillabaisse- und Paella-Fonds sowie Krabbenchips.',
      note: 'Schon eine Spur Garnelenfond in einem „vegetarischen“ Curry-Fond reicht für eine schwere Reaktion. Prüfen Sie gemeinsam genutzte Woks, Fritteusen und Fonds.',
    },
    eggs: {
      name: 'Eier',
      short: 'Mayo, Kuchen, Glasuren, Pudding',
      common: 'Mayonnaise, Quiche, Omelett, Kuchen, Baiser, frische Eierpasta, Pudding sowie panierte oder frittierte Speisen.',
      hidden: 'Eistreiche auf Gebäck und Brot, mit Eiweiß geklärte Consommés, Mousses, Marshmallows und Schaumtoppings auf Cocktails.',
      note: 'Eistreiche auf Backwaren übersieht man beim schnellen Blick auf die Speisekarte leicht. Kennzeichnen Sie es, statt sich auf die Beschreibung des Gebäcks zu verlassen.',
    },
    fish: {
      name: 'Fisch',
      short: 'Filets, Sardellen, Saucen',
      common: 'Fischfilets, Fischstäbchen, Sushi, Fischpastete, Räucherlachs und Sardellen.',
      hidden: 'Worcestersauce und Caesar-Dressing (Sardellen), Fischsauce in thailändischen und vietnamesischen Gerichten, manche Pizza- und Pastasaucen, Austernsauce-Mischungen und Brühwürfel.',
      note: 'Fischsauce und Worcestersauce sind die zwei klassischen unsichtbaren Fischquellen, die sich in sonst fleisch- oder gemüsebasierte Gerichte schleichen.',
    },
    peanuts: {
      name: 'Erdnüsse',
      short: 'Satay, Erdnussbutter, Arachisöl',
      common: 'Erdnussbutter, Satay-Sauce, geröstete und gesalzene Erdnüsse, Erdnussöl sowie viele asiatische und afrikanische Gerichte.',
      hidden: 'Chili- und Currysaucen, die mit gemahlenen Erdnüssen angereichert sind, Schokolade und Desserts von gemeinsam genutzten Produktionslinien sowie hydrolysiertes Pflanzenprotein.',
      note: '„Arachisöl“ ist nur ein anderer Name für Erdnussöl, und auch dieses muss deklariert werden. Raffiniertes Erdnussöl ist nicht ausgenommen - nur vollständig raffiniertes Sojaöl ist es.',
    },
    soybeans: {
      name: 'Sojabohnen',
      short: 'Sojasauce, Tofu, Lecithin',
      common: 'Sojasauce, Tofu, Edamame, Miso, Sojamilch und Tempeh.',
      hidden: 'Viele asiatische Saucen und Marinaden (Hoisin, Teriyaki), Pflanzenölmischungen, Fleischerzeugnisse mit Soja-Füllstoff, manche Brote sowie Sojalecithin (E322).',
      note: 'Sojalecithin (E322) steckt in einem großen Teil von Schokolade, Aufstrichen und Backwaren. Prüfen Sie die Lieferantenblätter, nicht nur die sichtbare Zutat.',
    },
    milk: {
      name: 'Milch',
      short: 'Käse, Butter, Sahne, Molke',
      common: 'Milch, Käse, Butter, Sahne, Joghurt und Speiseeis.',
      hidden: 'Milchpulver in manchen Broten und Teigen, Milchprotein-Bindemittel in Fleischerzeugnissen, Molkenwürze auf Chips, Natriumcaseinat in als „milchfrei“ beworbenen Toppings sowie Mehlschwitze-Saucen.',
      note: 'Milchprotein (Kasein oder Molke) taucht in als „milchfrei“ gekennzeichneten Produkten und in Brot auf. Gehen Sie nie ohne Prüfung davon aus, dass einfaches Brot oder eine trockene Gewürzmischung milchfrei ist.',
    },
    'tree-nuts': {
      name: 'Baumnüsse',
      short: 'Mandel, Haselnuss, Walnuss, Cashew',
      common: 'Mandeln, Haselnüsse, Walnüsse, Cashewkerne, Pekannüsse, Paranüsse, Pistazien und Macadamianüsse - in Kuchen, Pesto, Nussölen, Nussmilch und Praliné.',
      hidden: 'Pesto und Salatdressings, Nussöle zum Braten oder Verfeinern, Korma und andere mit gemahlenen Nüssen gebundene Saucen, Marzipan und Frangipane, Granola sowie Nusslikör.',
      note: 'Mandelmilch-Latte und Pesto-Pasta werden leicht übersehen. Beide verstecken Baumnüsse hinter einem Namen, der nicht „Nuss“ sagt.',
    },
    celery: {
      name: 'Sellerie',
      short: 'Knollensellerie, Fond, Würzung',
      common: 'Selleriestangen, Knollensellerie, Suppen, Eintöpfe und Salate.',
      hidden: 'Brühwürfel und Fond (Selleriesalz ist eine gängige Basis), Soßengranulat, Gewürzmischungen und Würzsalze, manche Würste sowie Fertigsaucen.',
      note: 'Selleriesalz versteckt sich in fast jedem handelsüblichen Brühwürfel und jeder Gewürzmischung. Prüfen Sie den Grundfond, bevor Sie eine Suppe oder Soße als sellerie-frei bezeichnen.',
    },
    mustard: {
      name: 'Senf',
      short: 'Gewürz, Dressings, Gewürzmischungen',
      common: 'Senf als Gewürz, Senfpulver, Salatdressings und Piccalilli.',
      hidden: 'Mayonnaise und viele Fertigsaucen, Marinaden und BBQ-Gewürzmischungen, Currypulver und Gewürzmischungen, manche Brote sowie Fleischerzeugnisse.',
      note: 'Senf ist eine versteckte Zutat in Mayonnaise und Marinaden. Er taucht selten im Namen des Gerichts auf, steckt aber sehr oft in der Sauce.',
    },
    sesame: {
      name: 'Sesamsamen',
      short: 'Brötchen, Tahini, Hummus',
      common: 'Sesamsamen auf Brot und Brötchen, Tahini, Hummus, Halva, Sesamöl und Falafel.',
      hidden: 'Samen, die auf der Unterseite von Brötchen mitgebacken sind, Gewürzmischungen (Za’atar, Dukkah), Dressings auf Tahini-Basis sowie gemeinsam genutzte Fritteusen mit sesampanierten Speisen.',
      note: 'Sesam wird oft auf der Unterseite eines Brötchens mitgebacken, nicht nur obenauf gestreut. Ein kurzer Blick auf das Brot kann das übersehen.',
    },
    sulphites: {
      name: 'Schwefeldioxid & Sulfite',
      short: 'Wein, Trockenfrüchte, Essig',
      common: 'Wein (besonders Weiß- und Roséwein), Trockenfrüchte wie Aprikosen und Rosinen, Sirup, Fruchtsaft und Cidre.',
      hidden: 'Fleischerzeugnisse und Würste, eingelegtes und konserviertes Gemüse, manche vorgeschnittenen und tiefgekühlten Kartoffelprodukte, essigbasierte Saucen sowie manche Erfrischungsgetränke.',
      note: 'Die gesetzliche Schwelle liegt bei einer Konzentration über 10 mg/kg bzw. 10 mg/l, nicht bei bloßer Anwesenheit. Getrocknete Aprikosen und günstiger Wein sind die zwei am häufigsten vergessenen Quellen.',
    },
    lupin: {
      name: 'Lupine',
      short: 'Lupinenmehl, Free-from-Mischungen',
      common: 'Lupinenmehl und Lupinensamen (Lupini), außerdem manche glutenfreien Brote und Backwaren.',
      hidden: 'Weizenmehlmischungen und Backmischungen, die Lupine als Proteinzusatz nutzen, manche Pasta, vegane Fleischersatzprodukte sowie kontinentale Brote und Gebäcke.',
      note: 'Lupine ist in der traditionellen britischen Küche selten, aber in kontinentalen Broten und glutenfreien Mehlmischungen häufig. Prüfen Sie importiertes oder als „frei von“ gekennzeichnetes Mehl.',
    },
    molluscs: {
      name: 'Weichtiere',
      short: 'Muscheln, Austern, Tintenfisch, Schnecken',
      common: 'Miesmuscheln, Venusmuscheln, Austern, Jakobsmuscheln, Tintenfisch, Oktopus und Schnecken (Escargot).',
      hidden: 'Austernsauce in Pfannengerichten und Marinaden, Meeresfrüchtefond in Paella und Risotto, Tintenfischtinte, die Pasta oder Reis färbt, sowie Surimi als Meeresfrüchte-Streckmittel.',
      note: 'Austernsauce ist die klassische versteckte Weichtierquelle. Sie taucht in Pfannengerichten und Marinaden auf, die sonst nur nach Fleisch oder Gemüse aussehen.',
    },
  },
};
